/**
 * Sommaire automatique des analyses de l'Agora.
 *
 * Les ancres sont dérivées des titres Markdown de niveau 2 et 3 : le praticien
 * pressé saute directement à « Portée pratique ». La génération est pure et
 * déterministe (même identifiant côté serveur et côté client) afin de ne pas
 * provoquer d'écart d'hydratation.
 */

export type TocEntry = {
  id: string;
  text: string;
  level: 2 | 3;
};

/** Identifiant d'ancre stable : ASCII minuscule, tirets, sans doublon. */
export function slugifyHeading(value: string): string {
  const base = value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return base.length > 0 ? base : "section";
}

/** Retire l'emphase Markdown la plus courante d'un intitulé. */
function plainText(value: string): string {
  return value
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_]{1,3}([^*_]+)[*_]{1,3}/g, "$1")
    .trim();
}

/**
 * Sommaire d'un contenu Markdown. Les blocs de code sont ignorés pour éviter
 * de prendre un commentaire `# ...` pour un titre.
 */
export function buildTableOfContents(content: string): TocEntry[] {
  const entries: TocEntry[] = [];
  const used = new Map<string, number>();
  let inFence = false;

  for (const line of content.split("\n")) {
    if (/^\s{0,3}(```|~~~)/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;

    const match = /^\s{0,3}(#{2,3})\s+(.+?)\s*#*\s*$/.exec(line);
    if (!match?.[1] || !match[2]) continue;

    const text = plainText(match[2]);
    if (text.length === 0) continue;

    const slug = slugifyHeading(text);
    const seen = used.get(slug) ?? 0;
    used.set(slug, seen + 1);

    entries.push({
      id: seen === 0 ? slug : `${slug}-${seen + 1}`,
      text,
      level: match[1].length === 2 ? 2 : 3,
    });
  }

  return entries;
}

/**
 * Table intitulé -> ancre, première occurrence gagnante. Utilisée par le rendu
 * Markdown pour poser l'attribut `id` sur les titres sans recalcul divergent.
 */
export function buildHeadingIdMap(toc: TocEntry[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const entry of toc) {
    const key = slugifyHeading(entry.text);
    if (!map.has(key)) map.set(key, entry.id);
  }
  return map;
}
